import React, { useEffect } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import styled from "styled-components/macro";

import { EIDReportAction } from "actions/eidReport";

import { FlippableFaIcon } from "components/FaIcon";
import { ChipElement } from "components/Chip/BaseChip";
import { CollapseLabel, FormCollapse } from "components/Form/FormCollapse";
import { Column, Row } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SecondaryButton } from "components/Form";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { SubtleBadge } from "components/Badge";

import { ModalTypes } from "constants/navigation";
import { getTransferMessage, NlisProgramName } from "constants/nlis";

import { formatWeightKg } from "lib";
import {
  formatUTCToLocalDateString,
  formatUTCToLocalDateTimeString,
} from "lib/timeFormats";

import { getContextByModalType } from "selectors";
import { getEIDReportByEID } from "selectors/eidReport";

import { useToggle } from "hooks";

const FILE_STATUS_COLORS: Record<string, string> = {
  Success: "success",
  Error: "errorRed",
  Pending: "warningOrange",
  "Pending Rollback": "warningOrange",
  "Rolled Back": "gray9C",
  UnSubmitted: "gray85",
};

const RESPONSE_TYPE_COLORS: Record<string, string> = {
  Success: "success",
  Warning: "warning",
  Failure: "errorRed",
  Error: "errorRed",
  Information: "primary",
};

const StatusChip = styled(ChipElement)`
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
  white-space: nowrap;
`;

const ClickableTableRow = styled(TableRow)`
  && {
    cursor: pointer;
    &:nth-child(even) {
      background: ${({ theme }) => theme.colors.grayF7};
    }
    &:hover {
      background: ${({ theme }) => theme.colors.blueHighlight};
    }
  }
`;

const DetailTableRow = styled(TableRow)`
  && {
    background: ${({ theme }) => theme.colors.grayF3};
  }
`;

const MutedTableCell = styled(TableCell)`
  && {
    color: ${({ theme }) => theme.colors.gray9C};
    font-style: italic;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: ${({ theme }) => `${theme.space[1]}px ${theme.space[3]}px`};
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.colors.gray9C};
  font-weight: 500;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  color: ${({ theme }) => theme.colors.gray9C};
  font-style: italic;
  padding: ${({ theme }) => `${theme.space[2]}px`};
`;

const PicArrow = styled.span`
  color: ${({ theme }) => theme.colors.gray9C};
  margin: 0 4px;
`;

const CollapseCell = styled(TableCell)`
  && {
    width: 32px;
  }
`;

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]}px;
`;

const TitleEid = styled.span`
  font-weight: normal;
  font-size: 0.85em;
  color: ${({ theme }) => theme.colors.gray44};
`;

function ScanEventRowOther({ scan }: { scan: EIDReportScanOther }) {
  return (
    <TableRow>
      <MutedTableCell>
        {scan.saleDate ? formatUTCToLocalDateString(scan.saleDate) : "-"}
      </MutedTableCell>
      <MutedTableCell>{scan.saleyardName ?? "****"}</MutedTableCell>
      <MutedTableCell>{scan.agencyName ?? "****"}</MutedTableCell>
      <MutedTableCell colSpan={2} />
      <TableCell>
        <Tooltip title="This scan event happened at a location not visible to you." />
      </TableCell>
    </TableRow>
  );
}

function ScanEventRowMine({ scan }: { scan: EIDReportScanMine }) {
  const [expanded, toggleExpanded] = useToggle(false);
  const { saleLotDetail } = scan;

  return (
    <>
      <ClickableTableRow onClick={toggleExpanded}>
        <TableCell>
          {saleLotDetail?.saleDate
            ? formatUTCToLocalDateString(saleLotDetail.saleDate)
            : "-"}
        </TableCell>
        <TableCell>{saleLotDetail?.saleyardName || "-"}</TableCell>
        <TableCell>{saleLotDetail?.agencyName || "-"}</TableCell>
        <TableCell>{saleLotDetail?.vendorPic || "-"}</TableCell>
        <TableCell>{saleLotDetail?.destinationPic || "-"}</TableCell>
        <CollapseCell>
          <FlippableFaIcon icon={faCaretUp} yflip={!expanded} color="gray9C" />
        </CollapseCell>
      </ClickableTableRow>
      {expanded && (
        <DetailTableRow>
          <TableCell colSpan={6}>
            <DetailGrid>
              {saleLotDetail?.buyerName && (
                <>
                  <DetailLabel>Buyer</DetailLabel>
                  <span>{saleLotDetail.buyerName}</span>
                </>
              )}
              {saleLotDetail?.vendorName && (
                <>
                  <DetailLabel>Vendor</DetailLabel>
                  <span>{saleLotDetail.vendorName}</span>
                </>
              )}
              {saleLotDetail != null && (
                <>
                  <DetailLabel>Lot #</DetailLabel>
                  <span>
                    {saleLotDetail.lotNumber ?? "-"}
                    {saleLotDetail.subLotNumber
                      ? `.${saleLotDetail.subLotNumber}`
                      : ""}
                  </span>
                </>
              )}
              {saleLotDetail?.auctionPenName && (
                <>
                  <DetailLabel>Pen</DetailLabel>
                  <span>{saleLotDetail.auctionPenName}</span>
                </>
              )}
              {scan.totalMassGrams != null && (
                <>
                  <DetailLabel>Weight</DetailLabel>
                  <span>{formatWeightKg(scan.totalMassGrams, true)}</span>
                </>
              )}
              <DetailLabel>Take</DetailLabel>
              <span>{scan.nlisTakeStatus || "-"}</span>
              <DetailLabel>Sell</DetailLabel>
              <span>{scan.nlisSellStatus || "-"}</span>
              <DetailLabel>P2P</DetailLabel>
              <span>{scan.nlisP2pStatus || "-"}</span>
              <DetailLabel>Sighted</DetailLabel>
              <span>{scan.isNlisSighted ? "Yes" : "No"}</span>
              {scan.erpStatus && (
                <>
                  <DetailLabel>ERP Status</DetailLabel>
                  <span>{scan.erpStatus}</span>
                </>
              )}
              {scan.euStatus && (
                <>
                  <DetailLabel>EU Status</DetailLabel>
                  <span>{scan.euStatus}</span>
                </>
              )}
              {saleLotDetail?.nlisPrograms &&
                saleLotDetail.nlisPrograms.length > 0 && (
                  <>
                    <DetailLabel>NLIS Programs</DetailLabel>
                    <span>
                      {saleLotDetail.nlisPrograms
                        .map(
                          p =>
                            `${NlisProgramName[p.code] || p.code} (${p.status})`,
                        )
                        .join(", ")}
                    </span>
                  </>
                )}
            </DetailGrid>
          </TableCell>
        </DetailTableRow>
      )}
    </>
  );
}
function ScanEventRow({ scan }: { scan: EIDReportScan }) {
  if (scan.isMine === true) {
    return <ScanEventRowMine scan={scan as EIDReportScanMine} />;
  }
  return <ScanEventRowOther scan={scan as EIDReportScanOther} />;
}

function NlisEventRow({ transfer }: { transfer: EIDReportNlisTransfer }) {
  const [expanded, toggleExpanded] = useToggle(false);
  const { transferDetail } = transfer;

  const submittedBy =
    transfer.submittedByFirstName || transfer.submittedByLastName
      ? `${transfer.submittedByFirstName || ""} ${transfer.submittedByLastName || ""}`.trim()
      : null;

  const messageText =
    transferDetail?.messageNo != null
      ? getTransferMessage(transferDetail.messageNo)
      : null;

  return (
    <>
      <ClickableTableRow onClick={toggleExpanded}>
        <TableCell>
          {transfer.created
            ? formatUTCToLocalDateTimeString(transfer.created)
            : "-"}
        </TableCell>
        <TableCell>{transfer.fileType}</TableCell>
        <TableCell>{transfer.saleyardName || "-"}</TableCell>
        <TableCell>{transfer.saleCode ?? "-"}</TableCell>
        <TableCell>
          {transferDetail?.sourcePic || "-"}
          {(transferDetail?.sourcePic || transferDetail?.destinationPic) && (
            <PicArrow>→</PicArrow>
          )}
          {transferDetail?.destinationPic || ""}
        </TableCell>
        <TableCell>
          <StatusChip
            $backgroundColor={
              FILE_STATUS_COLORS[transfer.fileStatus] || "gray9C"
            }
            $color="white"
          >
            {transfer.fileStatus}
          </StatusChip>
        </TableCell>
        <TableCell>
          {transferDetail?.responseType ? (
            <StatusChip
              $backgroundColor={
                RESPONSE_TYPE_COLORS[transferDetail.responseType] || "gray9C"
              }
              $color="white"
            >
              {transferDetail.responseType}
            </StatusChip>
          ) : (
            "-"
          )}
        </TableCell>
        <CollapseCell>
          <FlippableFaIcon icon={faCaretUp} yflip={!expanded} color="gray9C" />
        </CollapseCell>
      </ClickableTableRow>
      {expanded && (
        <DetailTableRow>
          <TableCell colSpan={8}>
            <DetailGrid>
              {transfer.transactionId && (
                <>
                  <DetailLabel>Transaction ID</DetailLabel>
                  <span>{transfer.transactionId}</span>
                </>
              )}
              {submittedBy && (
                <>
                  <DetailLabel>Submitted By</DetailLabel>
                  <span>{submittedBy}</span>
                </>
              )}
              {transferDetail?.messageNo != null && (
                <>
                  <DetailLabel>Message #</DetailLabel>
                  <span>{transferDetail.messageNo}</span>
                </>
              )}
              {messageText && (
                <>
                  <DetailLabel>Message</DetailLabel>
                  <span>{messageText}</span>
                </>
              )}
              {transfer.created && (
                <>
                  <DetailLabel>Created</DetailLabel>
                  <span>
                    {formatUTCToLocalDateTimeString(transfer.created)}
                  </span>
                </>
              )}
              {transfer.submitted && (
                <>
                  <DetailLabel>Submitted</DetailLabel>
                  <span>
                    {formatUTCToLocalDateTimeString(transfer.submitted)}
                  </span>
                </>
              )}
              {transfer.processed && (
                <>
                  <DetailLabel>Processed</DetailLabel>
                  <span>
                    {formatUTCToLocalDateTimeString(transfer.processed)}
                  </span>
                </>
              )}
              {transfer.errorNotes && (
                <>
                  <DetailLabel>Notes</DetailLabel>
                  <span>{transfer.errorNotes}</span>
                </>
              )}
            </DetailGrid>
          </TableCell>
        </DetailTableRow>
      )}
    </>
  );
}

function ScansSection({ scans }: { scans: EIDReportScan[] }) {
  const [isOpen, toggleOpen] = useToggle(true);

  const header = (
    <Row>
      <SubtleBadge>Scan Events</SubtleBadge>
      <CollapseLabel>
        {scans.length} scan{scans.length !== 1 ? "s" : ""}
      </CollapseLabel>
    </Row>
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={toggleOpen}
      header={header}
      id="eid-scans"
    >
      {scans.length === 0 ? (
        <EmptyRow>No scan events.</EmptyRow>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sale Date</TableCell>
                <TableCell>Saleyard</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Source PIC</TableCell>
                <TableCell>Dest PIC</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.map((scan, i) => (
                <ScanEventRow key={i} scan={scan} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </FormCollapse>
  );
}

function NlisSection({ transfers }: { transfers: EIDReportNlisTransfer[] }) {
  const [isOpen, toggleOpen] = useToggle(true);

  const header = (
    <Row>
      <SubtleBadge>NLIS Events</SubtleBadge>
      <CollapseLabel>
        {transfers.length} event{transfers.length !== 1 ? "s" : ""}
      </CollapseLabel>
    </Row>
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={toggleOpen}
      header={header}
      id="eid-nlis"
    >
      {transfers.length === 0 ? (
        <EmptyRow>No NLIS events.</EmptyRow>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Saleyard</TableCell>
                <TableCell>Sale</TableCell>
                <TableCell>Source → Dest PIC</TableCell>
                <TableCell>File Status</TableCell>
                <TableCell>EID Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map((t, i) => (
                <NlisEventRow key={i} transfer={t} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </FormCollapse>
  );
}

interface EIDReportModalProps {
  onClose: () => void;
}

export function EIDReportModal({ onClose }: EIDReportModalProps) {
  const dispatch = useDispatch();
  const context =
    useSelector(getContextByModalType(ModalTypes.EIDReport)) || {};
  const eid: string | null = context?.eid ?? null;
  const forceStaff: boolean = context?.forceStaff ?? false;

  const report = useSelector(getEIDReportByEID(eid));

  useEffect(() => {
    if (eid) {
      dispatch(EIDReportAction.request({ ids: [eid], forceStaff }));
    }
  }, [dispatch, eid, forceStaff]);

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>
        <TitleRow>
          EID Report
          {eid && <TitleEid>{eid}</TitleEid>}
          {report?.isStaff && (
            <StatusChip $backgroundColor="primary" $color="white">
              Staff view
            </StatusChip>
          )}
        </TitleRow>
      </DialogTitle>

      <DialogContent dividers>
        {!report ? (
          <LoadingSpinner size={24} subjectName="EID Report" />
        ) : report.scans.length === 0 && report.nlisTransfers.length === 0 ? (
          <EmptyRow>This EID has never been seen in AgriNous.</EmptyRow>
        ) : (
          <Column>
            {report.nlisId && (
              <Row padding={1} alignCenter>
                <SubtleBadge>NLIS ID: {report.nlisId}</SubtleBadge>
              </Row>
            )}
            <ScansSection scans={report.scans} />
            <NlisSection transfers={report.nlisTransfers} />
          </Column>
        )}
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
}
